import * as I from "immutable";
import { createSelector } from "reselect";

import { Action, actionCreators } from "../actions";
import { mapFilter, mapMap, mapUpdateV } from "../utils/immutable";

import { RootState } from ".";
import { getMonologUserId } from "./roster";

type roomId = string;
type ts = number;
export type Receipts = { ts: number; userIds: string[]; userCount: number };
type ReceiptByTs = I.Map<ts, Receipts>;

export interface Store {
    receiptsByRoomId: I.Map<roomId, ReceiptByTs>;
    loadingByRoomId: I.Set<roomId>;
}

export function initialStore(): Store {
    return {
        receiptsByRoomId: I.Map(),
        loadingByRoomId: I.Set(),
    };
}

export function update(store = initialStore(), action: Action): Store {
    switch (action.type) {
        case actionCreators.onLogout.type:
        case actionCreators.onWrongToken.type: {
            return initialStore();
        }
        case actionCreators.onReceipts.type: {
            const { room_id: roomId, receipts } = action.payload;
            const receiptsByRoomId = mergeReceipts(
                store,
                roomId,
                receipts.map(({ ts, user_count: userCount, user_ids: userIds }) => ({
                    ts,
                    userIds,
                    userCount: userCount || userIds.length,
                }))
            );
            return { ...store, receiptsByRoomId };
        }

        case actionCreators.setReceiptsEnqueued.type: {
            // let's do optimistic update here
            const { roomId, ts, monologUserId } = action.payload;
            const roomReceipts = store.receiptsByRoomId.get(roomId) || I.Map();
            const receiptsByTs = roomReceipts.get(ts) || { ts, userIds: [], userCount: 0 };
            if (receiptsByTs.userIds.indexOf(monologUserId) === -1) {
                receiptsByTs.userIds.push(monologUserId);
                receiptsByTs.userCount++;
            }
            const receiptsByRoomId = mergeReceipts(store, roomId, [receiptsByTs]);
            return { ...store, receiptsByRoomId };
        }
        case actionCreators.setReceiptsStarted.type: {
            const { roomId } = action.payload;
            return { ...store, loadingByRoomId: store.loadingByRoomId.add(roomId) };
        }
        case actionCreators.setReceiptsSucceeded.type:
        case actionCreators.setReceiptsFailed.type: {
            const { roomId } = action.payload;
            return { ...store, loadingByRoomId: store.loadingByRoomId.delete(roomId) };
        }
        default: {
            return store;
        }
    }
}

function mergeReceipts(store: Store, roomId: string, receipts: Receipts[]) {
    const { receiptsByRoomId } = store;
    return mapUpdateV(receiptsByRoomId, roomId, I.Map(), roomReceipts => {
        const out = cleanReceipts(roomReceipts, receipts);
        return receipts.reduce((acc, receiptsByTs) => {
            const { ts } = receiptsByTs;
            return acc.set(ts, receiptsByTs);
        }, out);
    });
}

function cleanReceipts(receipts: ReceiptByTs, newItems: Receipts[]) {
    const newUserIds = newItems.reduce<string[]>((acc, x) => acc.concat(x.userIds), []);
    return mapFilter(
        mapMap(receipts, x => {
            const userIds = x.userIds.filter(id => newUserIds.indexOf(id) === -1);
            const deletedNum = x.userIds.length - userIds.length;
            if (deletedNum) {
                return { ts: x.ts, userIds, userCount: x.userCount - deletedNum };
            } else {
                return x;
            }
        }),
        x => !!x.userIds.length
    );
}

export type OuterStore = { history: Store };

const DEBUG_SELF = false;

function getReceiptsByRoomId(store: RootState) {
    return store.history.receiptsByRoomId;
}

const receiptsSelector = createSelector(
    getReceiptsByRoomId,
    getMonologUserId,
    (receipts, monologUserId) =>
        mapMap(receipts, receiptsByTs => {
            let myTs: number | undefined;
            const receiptsByTsWithoutMonolog = mapMap(
                receiptsByTs,
                ({ userCount: allUserCount, userIds: allUserIds }, ts) => {
                    const hasSelf =
                        (monologUserId && allUserIds.indexOf(monologUserId) !== -1) || false;
                    let userIds = allUserIds;
                    let userCount = allUserCount;
                    if (hasSelf) {
                        myTs = ts;
                        userIds = allUserIds.filter(id => id !== monologUserId || DEBUG_SELF);
                        userCount = userCount - 1;
                    }
                    if (userIds.length) {
                        return { ts, userCount, userIds };
                    }
                    return;
                }
            );
            return { myTs, receiptsByTsWithoutMonolog };
        })
);

export function getReceipts(store: RootState, roomId: string) {
    return receiptsSelector(store).get(roomId);
}

export function isLoadingByRoomId(store: OuterStore, roomId: string) {
    return store.history.loadingByRoomId.has(roomId);
}
